import { formatDate } from './formatters';
import { ColumnDefinition } from '../features/table-preferences/types';
import { pickSurchargeForDate } from '../features/fuel-surcharge/api';
import type { FuelSurchargeWeek } from '../features/fuel-surcharge/types';

function surchargeWeekForRow(row: any, weeks: FuelSurchargeWeek[]): FuelSurchargeWeek | null {
  if (!weeks.length) {
    return null;
  }

  const deliveryDateISO = row?.deliveryDate ? String(row.deliveryDate).slice(0, 10) : '';

  const match = deliveryDateISO ? pickSurchargeForDate(weeks, deliveryDateISO) : null;

  return match?.week ?? weeks.find((week) => week.isCurrent) ?? weeks[weeks.length - 1] ?? null;
}

function stonePriceCentsForRow(row: any): number | null {
  const warehouseId = row?.location?.id ?? row?.locationId ?? null;

  if (!warehouseId) {
    return null;
  }

  const value = row?.pricing?.stonePriceByLocation?.[warehouseId];

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function buildOrgOrderExtractors(
  fuelSurchargeWeeks: FuelSurchargeWeek[]
): Record<string, (row: any) => string> {
  return {
    'load-number': (row) => row.loadNumber ?? '',

    tags: (row) => (Array.isArray(row.order?.tags) ? row.order.tags.join(', ') : ''),

    customer: (row) => row.cust?.name ?? '',

    'sales-rep': (row) => row.order?.deliveryAddress?.salesRep?.email ?? '',

    'po-number': (row) => row.order?.poNumber ?? '',

    tons: (row) => (row.quantity != null ? String(row.quantity) : ''),

    'ticket-number': (row) => row.ticketNumber ?? '',

    date: (row) => {
      const dt = row.orderCreatedAt || row.order?.createdAt;

      return dt ? formatDate(dt) : '';
    },

    status: (row) => row.status ?? '',

    carrier: (row) => row.carrier?.companyName ?? '',

    product: (row) => row.product?.name ?? '',

    'fulfillment-method': (row) => {
      const name = row.orgLoadOptionNameSnapshot;

      if (!name) {
        return '';
      }

      return name.toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
    },

    'pickup-date': (row) => (row.pickupDate ? formatDate(row.pickupDate) : ''),

    'delivery-date': (row) => (row.deliveryDate ? formatDate(row.deliveryDate) : ''),

    'carrier-rate': (row) =>
      row.deliveryFeeSnapshot != null ? String(row.deliveryFeeSnapshot / 100) : '',

    'delivery-pct': (row) => {
      const week = surchargeWeekForRow(row, fuelSurchargeWeeks);

      return week ? String(week.deliveryPct) : '';
    },

    'material-pct': (row) => {
      const week = surchargeWeekForRow(row, fuelSurchargeWeeks);

      return week ? String(week.materialPct) : '';
    },

    'stone-price': (row) => {
      const cents = stonePriceCentsForRow(row);

      return cents != null ? (cents / 100).toFixed(2) : '';
    },

    'pickup-location': (row) => row.location?.name ?? '',

    'pickup-state': (row) => row.location?.state ?? '',

    'pickup-city': (row) => row.location?.city ?? '',

    'delivery-state': (row) => row.order?.deliveryAddress?.state ?? '',

    'delivery-city': (row) => row.order?.deliveryAddress?.city ?? '',

    'resale-cert': (row) => {
      const doc = row.cust?.documents?.find((d: any) => d.type === 'RESALE_CERTIFICATE');

      if (!doc) {
        return 'No';
      }

      return doc.status === 'REJECTED' ? 'Rejected' : 'Yes';
    },

    'credit-app': (row) => {
      const doc = row.cust?.documents?.find((d: any) => d.type === 'AGREEMENT');

      if (!doc) {
        return 'No';
      }

      return doc.status === 'REJECTED' ? 'Rejected' : 'Yes';
    },

    driver: (row) => row.driverName ?? '',

    'truck-number': (row) => row.truckNumber ?? '',

    'created-by': (row) => {
      const creator = row?.createdBy;

      if (!creator) {
        return '';
      }

      const role =
        creator.actorRole === 'ORG_ADMIN'
          ? 'Org Admin'
          : creator.actorRole === 'ORG_OWNER'
            ? 'Org Owner'
            : creator.actorRole === 'CUSTOMER' || creator.actorRole === 'SUBUSER'
              ? 'Customer'
              : creator.actorRole?.replace(/_/g, ' ') || 'Unknown';

      const name = creator.actorName?.trim();
      const email = creator.actorEmail?.trim();

      if (name && email) {
        return `${name} (${email}) — ${role}`;
      }

      if (name) {
        return `${name} — ${role}`;
      }

      if (email) {
        return `${email} — ${role}`;
      }

      return role;
    },
  };
}

function escapeCell(val: string): string {
  return `"${String(val).replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, headers: string[], csvRows: string[]): void {
  const csv = [headers.join(','), ...csvRows].join('\n');

  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}

export function downloadOrgOrdersCsv(
  rows: any[],
  visibleColumns: ColumnDefinition<any>[],
  fuelSurchargeWeeks: FuelSurchargeWeek[] = []
): void {
  const exportColumns = visibleColumns.filter((col) => col.id !== 'actions');

  const orgOrderExtractors = buildOrgOrderExtractors(fuelSurchargeWeeks);

  const headers = exportColumns.map((col) => escapeCell(col.header));

  const csvRows = rows.map((row: any) =>
    exportColumns
      .map((col) => {
        const extractor = orgOrderExtractors[col.id];

        return escapeCell(extractor ? extractor(row) : '');
      })
      .join(',')
  );

  downloadCsv(`org-orders-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
}

// ─── Customer orders CSV ─────────────────────────────────────────────────────

const CUSTOMER_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  WAITLIST: 'Waitlist',
  APPROVED: 'Processing',
  ASSIGNED: 'Assigned',
  READY_FOR_PICKUP: 'Ready for Pickup',
  ENROUTE: 'Enroute',
  DELIVERED: 'Delivered',
  INVOICED: 'Invoiced',
  CUSTOMER_CANCELLED: 'Cancelled',
  PREPAY_REQUIRED: 'Prepay Required',
  PICKED_UP: 'Picked Up',
  ON_HOLD: 'Frozen',
};

function customerStatusLabel(status?: string | null): string {
  if (!status) {
    return '';
  }

  return CUSTOMER_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

function formatCustomerDeliveryAddress(address: any): string {
  if (!address) {
    return '';
  }

  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, address.postalCode].filter(Boolean).join(' ');

  return [address.fullName, address.line1, address.line2, cityStateZip, address.country]
    .filter(Boolean)
    .join(', ');
}

function buildCustomerOrderExtractors(): Record<string, (row: any) => string> {
  return {
    'load-number': (row) => row.loadNumber ?? '',

    'po-number': (row) => row.poNumber ?? row.order?.poNumber ?? '',

    date: (row) => {
      const dt = row.createdAt ?? row.orderCreatedAt ?? row.order?.createdAt;

      return dt ? formatDate(dt) : '';
    },

    status: (row) => customerStatusLabel(row.status),

    'delivery-address': (row) =>
      formatCustomerDeliveryAddress(row.deliveryAddress ?? row.order?.deliveryAddress),

    product: (row) => row.product?.name ?? '',
  };
}

function flattenCustomerOrders(orders: any[]): any[] {
  return orders.flatMap((order: any) =>
    (order.items ?? []).map((item: any) => ({
      ...item,
      orderId: order.id,
      orderCreatedAt: order.createdAt,
      createdAt: order.createdAt ?? item.createdAt,
      poNumber: order.poNumber,
      deliveryAddressId: order.deliveryAddressId,
      deliveryAddress: order.deliveryAddress,
      userId: order.userId,
      unseenNoteCount: order.unseenNoteCount ?? 0,
      order,
    }))
  );
}

/**
 * Customer-side CSV export.
 *
 * Data fetching intentionally does NOT live here. The app's architecture only
 * permits features/<domain>/api.ts to import the axios client, so OrdersPage
 * fetches the complete export dataset through features/customer-orders/api.ts
 * and hands the resulting orders to this formatting/downloading utility.
 *
 * Pass baseColumns, not visibleColumns, so the CSV always contains every
 * customer data column even when one is hidden through Column Manager.
 */
export function downloadCustomerOrdersCsv(
  orders: any[],
  allColumns: ColumnDefinition<any>[]
): number {
  const rows = flattenCustomerOrders(orders);

  if (rows.length === 0) {
    throw new Error('No orders match the current filters.');
  }

  const exportColumns = allColumns.filter((col) => col.id !== 'actions');

  const extractors = buildCustomerOrderExtractors();

  const headers = exportColumns.map((col) => escapeCell(col.header));

  const csvRows = rows.map((row: any) =>
    exportColumns
      .map((col) => {
        const extractor = extractors[col.id];

        return escapeCell(extractor ? extractor(row) : '');
      })
      .join(',')
  );

  downloadCsv(`customer-orders-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);

  return rows.length;
}

// ─── Admin customers CSV ─────────────────────────────────────────────────────

type AdminCustomerTierCsvOption = {
  id: string;
  name: string;
};

function adminCustomerStatusLabel(status?: string | null): string {
  if (!status) {
    return '';
  }

  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function adminCustomerDocumentStatus(
  customer: any,
  type: 'AGREEMENT' | 'RESALE_CERTIFICATE'
): string {
  const document = customer?.documents?.find((item: any) => item.type === type);

  if (!document) {
    return 'No';
  }

  return document.status === 'REJECTED' ? 'Rejected' : 'Yes';
}

function buildAdminCustomerExtractors(
  tiers: AdminCustomerTierCsvOption[]
): Record<string, (row: any) => string> {
  const tierNameById = new Map(tiers.map((tier) => [tier.id, tier.name]));

  return {
    name: (row) => row.name ?? row.email ?? '',

    email: (row) => row.email ?? '',

    'hq-state': (row) => row.hqState ?? '',

    tier: (row) => (row.tierId ? (tierNameById.get(row.tierId) ?? '') : ''),

    // Keep financial values numeric so Excel/Sheets can calculate with them.
    'credit-limit': (row) =>
      row.blumeCreditLimit !== null && row.blumeCreditLimit !== undefined
        ? String(row.blumeCreditLimit)
        : '',

    'available-credit': (row) =>
      row.blumeCreditAvailable !== null && row.blumeCreditAvailable !== undefined
        ? String(row.blumeCreditAvailable)
        : '',

    'overdue-balance': (row) =>
      row.blumeOverdueInvoiceBalance !== null && row.blumeOverdueInvoiceBalance !== undefined
        ? String(row.blumeOverdueInvoiceBalance)
        : '',

    status: (row) => adminCustomerStatusLabel(row.status),

    'resale-cert': (row) => adminCustomerDocumentStatus(row, 'RESALE_CERTIFICATE'),

    'credit-app': (row) => adminCustomerDocumentStatus(row, 'AGREEMENT'),

    updated: (row) => {
      if (!row.updatedAt) {
        return '';
      }

      const date = new Date(row.updatedAt);

      return Number.isNaN(date.getTime()) ? String(row.updatedAt) : date.toLocaleString();
    },
  };
}

/**
 * Export every admin customer-table data column except Actions.
 *
 * The caller supplies the full filtered result set; this utility only formats
 * and downloads the CSV and does not perform HTTP requests.
 */
export function downloadAdminCustomersCsv(
  rows: any[],
  columns: ColumnDefinition<any>[],
  tiers: AdminCustomerTierCsvOption[] = []
): number {
  if (rows.length === 0) {
    throw new Error('No customers match the current filters.');
  }

  const exportColumns = columns.filter((column) => column.id !== 'actions');
  const extractors = buildAdminCustomerExtractors(tiers);

  const headers = exportColumns.map((column) => escapeCell(column.header));

  const csvRows = rows.map((row) =>
    exportColumns
      .map((column) => {
        const extractor = extractors[column.id];

        return escapeCell(extractor ? extractor(row) : '');
      })
      .join(',')
  );

  downloadCsv(`customers-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);

  return rows.length;
}
